import { useEffect, useState } from 'react'
import { CheckCircle2, ChevronRight, Loader2, Plus, Search, Sparkles, X } from 'lucide-react'
import type { CreateRewardDraft, LearnerReward, RewardDraft } from '../model/reward'
import { ssoUsersApi, type SsoUser } from '../api/ssoUsersApi'

interface RewardModalProps {
  item?: LearnerReward | null
  busy: boolean
  onClose: () => void
  onSave?: (userId: number, draft: RewardDraft) => Promise<void>
  onCreate?: (draft: CreateRewardDraft) => Promise<LearnerReward>
}

export function RewardModal({
  item,
  busy,
  onClose,
  onSave,
  onCreate,
}: RewardModalProps) {
  const isDirectEdit = Boolean(item)

  // Step 1: Tạo bản ghi qua Email SSO | Step 2: Chỉnh sửa điểm & Streak
  const [step, setStep] = useState<1 | 2>(isDirectEdit ? 2 : 1)

  // Step 1 state
  const [searchEmail, setSearchEmail] = useState('')
  const [isSearchingSso, setIsSearchingSso] = useState(false)
  const [ssoUser, setSsoUser] = useState<SsoUser | null>(null)
  const [ssoError, setSsoError] = useState<string | null>(null)

  // Step 2 state (bản ghi đang được edit, có thể là item truyền vào hoặc bản ghi vừa tạo)
  const [activeItem, setActiveItem] = useState<LearnerReward | null>(item ?? null)
  const [draft, setDraft] = useState<RewardDraft>({
    earned_reward: item?.earned_reward ?? 0,
    spent_reward: item?.spent_reward ?? 0,
    current_streak: item?.current_streak ?? 0,
    longest_streak: item?.longest_streak ?? 0,
    last_checkin_date: item?.last_checkin_date ?? null,
  })
  const [justCreated, setJustCreated] = useState(false)

  useEffect(() => {
    if (item) {
      setActiveItem(item)
      setDraft({
        earned_reward: item.earned_reward ?? 0,
        spent_reward: item.spent_reward ?? 0,
        current_streak: item.current_streak ?? 0,
        longest_streak: item.longest_streak ?? 0,
        last_checkin_date: item.last_checkin_date ?? null,
      })
      setStep(2)
    }
  }, [item])

  async function handleSearchSso(e?: React.FormEvent) {
    if (e) e.preventDefault()
    const trimmed = searchEmail.trim()
    if (!trimmed) {
      setSsoError('Vui lòng nhập địa chỉ email người học.')
      return
    }

    setSsoError(null)
    setIsSearchingSso(true)
    setSsoUser(null)

    try {
      const user = await ssoUsersApi.searchByEmail(trimmed)
      if (!user) {
        setSsoError(`Không tìm thấy tài khoản với email "${trimmed}" trên SSO.`)
      } else {
        setSsoUser(user)
      }
    } catch (err: unknown) {
      setSsoError(err instanceof Error ? err.message : 'Lỗi khi tra cứu tài khoản trên SSO.')
    } finally {
      setIsSearchingSso(false)
    }
  }

  async function handleCreateRecord() {
    if (!ssoUser || !onCreate) return

    try {
      const newReward = await onCreate({
        user_id: ssoUser.id,
        email: ssoUser.email,
        earned_reward: 0,
        spent_reward: 0,
        current_streak: 0,
        longest_streak: 0,
        last_checkin_date: null,
      })

      setActiveItem(newReward)
      setDraft({
        earned_reward: newReward.earned_reward ?? 0,
        spent_reward: newReward.spent_reward ?? 0,
        current_streak: newReward.current_streak ?? 0,
        longest_streak: newReward.longest_streak ?? 0,
        last_checkin_date: newReward.last_checkin_date ?? null,
      })
      setJustCreated(true)
      setStep(2)
    } catch {
      // toast error handled by caller
    }
  }

  async function handleSaveDraft(e: React.FormEvent) {
    e.preventDefault()
    if (!activeItem || !onSave) return

    await onSave(activeItem.user_id, draft)
    if (isDirectEdit) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-950/45 p-4 max-[620px]:items-end max-[620px]:p-0"
      onMouseDown={(event) => event.currentTarget === event.target && onClose()}
    >
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl max-[620px]:rounded-b-none">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-orange-50 text-orange-500">
              {step === 1 ? <Plus size={18} /> : <Sparkles size={18} />}
            </span>
            <div>
              <h2 className="text-base font-bold text-slate-900">
                {isDirectEdit
                  ? 'Điều chỉnh điểm thưởng'
                  : step === 1
                  ? 'Bước 1: Tìm tài khoản & Tạo bản ghi'
                  : 'Bước 2: Điều chỉnh điểm thưởng & Streak'}
              </h2>
              <p className="font-mono text-xs text-slate-400">
                {step === 1
                  ? 'Tra cứu người dùng qua email SSO'
                  : activeItem?.email || `Tài khoản #${activeItem?.user_id}`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </header>

        {/* BODY */}
        {step === 1 ? (
          /* STEP 1: Nhập email & tìm tài khoản SSO */
          <div className="p-6">
            <form onSubmit={handleSearchSso} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Email người học <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      required
                      autoFocus
                      placeholder="VD: learner@hocnhe.com"
                      value={searchEmail}
                      onChange={(e) => {
                        setSearchEmail(e.target.value)
                        setSsoUser(null)
                        setSsoError(null)
                      }}
                      className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-3 text-sm text-slate-800 outline-none transition focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSearchingSso || !searchEmail.trim()}
                    className="button-secondary inline-flex items-center gap-1.5 text-xs whitespace-nowrap"
                  >
                    {isSearchingSso ? (
                      <>
                        <Loader2 size={14} className="animate-spin text-orange-500" />
                        Đang tìm…
                      </>
                    ) : (
                      'Kiểm tra SSO'
                    )}
                  </button>
                </div>
                <p className="mt-1.5 text-[11px] text-slate-400">
                  Hệ thống sẽ gọi SSO để tự động lấy User ID tương ứng với email này.
                </p>
              </div>

              {ssoError && (
                <div className="rounded-xl border border-red-100 bg-red-50 p-3 text-xs font-semibold text-red-600">
                  {ssoError}
                </div>
              )}

              {/* Card thông tin user tìm thấy trên SSO */}
              {ssoUser && (
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 transition animate-fadeIn">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-100 text-emerald-700 font-bold text-sm">
                      {ssoUser.display_name
                        ? ssoUser.display_name.slice(0, 1).toUpperCase()
                        : ssoUser.email.slice(0, 1).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-800">
                        {ssoUser.display_name || ssoUser.email}
                      </p>
                      <p className="truncate text-xs text-slate-500">{ssoUser.email}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="inline-block rounded-md bg-emerald-100 px-2 py-0.5 font-mono text-xs font-bold text-emerald-800">
                      User ID: #{ssoUser.id}
                    </span>
                    <p className="mt-0.5 text-[11px] text-slate-400 capitalize">
                      Vai trò: {ssoUser.role}
                    </p>
                  </div>
                </div>
              )}
            </form>

            <footer className="mt-6 flex justify-end gap-2 border-t border-slate-100 pt-4">
              <button type="button" onClick={onClose} className="button-secondary text-xs">
                Huỷ
              </button>
              <button
                type="button"
                disabled={!ssoUser || busy}
                onClick={handleCreateRecord}
                className="button-primary inline-flex items-center gap-1.5 text-xs"
              >
                {busy ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Đang tạo…
                  </>
                ) : (
                  <>
                    Tạo bản ghi
                    <ChevronRight size={14} />
                  </>
                )}
              </button>
            </footer>
          </div>
        ) : (
          /* STEP 2: Chỉnh sửa điểm & Streak */
          <form onSubmit={handleSaveDraft}>
            <div className="p-6">
              {justCreated && (
                <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-xs text-emerald-800">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600" />
                  <div>
                    <strong className="block font-semibold">Đã tạo bản ghi thành công!</strong>
                    <span>
                      Tài khoản <strong>{activeItem?.email}</strong> (User ID #{activeItem?.user_id}) đã sẵn sàng. Bạn có thể điều chỉnh điểm thưởng và streak bên dưới để test hoặc đóng lại để sửa sau.
                    </span>
                  </div>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="field">
                  Tổng điểm đã nhận
                  <input
                    type="number"
                    min="0"
                    value={draft.earned_reward}
                    onChange={(e) => setDraft({ ...draft, earned_reward: Number(e.target.value) })}
                  />
                </label>

                <label className="field">
                  Điểm đã sử dụng
                  <input
                    type="number"
                    min="0"
                    value={draft.spent_reward}
                    onChange={(e) => setDraft({ ...draft, spent_reward: Number(e.target.value) })}
                  />
                </label>

                <label className="field">
                  Chuỗi ngày học hiện tại (Streak)
                  <input
                    type="number"
                    min="0"
                    value={draft.current_streak}
                    onChange={(e) => setDraft({ ...draft, current_streak: Number(e.target.value) })}
                  />
                </label>

                <label className="field">
                  Chuỗi dài nhất đạt được
                  <input
                    type="number"
                    min="0"
                    value={draft.longest_streak}
                    onChange={(e) => setDraft({ ...draft, longest_streak: Number(e.target.value) })}
                  />
                </label>

                <label className="field sm:col-span-2">
                  Ngày điểm danh gần nhất
                  <input
                    type="date"
                    value={draft.last_checkin_date ?? ''}
                    onChange={(e) =>
                      setDraft({ ...draft, last_checkin_date: e.target.value || null })
                    }
                  />
                </label>
              </div>
            </div>

            <footer className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
              <button type="button" onClick={onClose} className="button-secondary text-xs">
                {justCreated ? 'Hoàn tất & Đóng' : 'Huỷ'}
              </button>
              <button disabled={busy} className="button-primary inline-flex items-center gap-1.5 text-xs">
                {busy ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Đang lưu…
                  </>
                ) : (
                  'Lưu thay đổi'
                )}
              </button>
            </footer>
          </form>
        )}
      </div>
    </div>
  )
}
